import { Sun, CloudSun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, Compass, MapPin, ArrowUp, ArrowDown, Drop } from "@phosphor-icons/react";
import useWeather from "@/hooks/useWeather";

/**
 * Compact weather chip for the Morning Brief masthead.
 *
 * Reads from the shared `useWeather` hook so it stays perfectly in
 * sync with the SunriseSunsetRail below the greeting (single fetch,
 * single geolocation prompt, single cache).
 */

// WMO weather codes → { label, icon component }. Ref:
// https://open-meteo.com/en/docs (weather_code)
const WMO = {
  0: { label: "Clear", Icon: Sun },
  1: { label: "Mainly clear", Icon: Sun },
  2: { label: "Partly cloudy", Icon: CloudSun },
  3: { label: "Overcast", Icon: Cloud },
  45: { label: "Fog", Icon: CloudFog },
  48: { label: "Rime fog", Icon: CloudFog },
  51: { label: "Light drizzle", Icon: CloudRain },
  53: { label: "Drizzle", Icon: CloudRain },
  55: { label: "Heavy drizzle", Icon: CloudRain },
  61: { label: "Light rain", Icon: CloudRain },
  63: { label: "Rain", Icon: CloudRain },
  65: { label: "Heavy rain", Icon: CloudRain },
  71: { label: "Light snow", Icon: CloudSnow },
  73: { label: "Snow", Icon: CloudSnow },
  75: { label: "Heavy snow", Icon: CloudSnow },
  77: { label: "Snow grains", Icon: CloudSnow },
  80: { label: "Rain showers", Icon: CloudRain },
  81: { label: "Rain showers", Icon: CloudRain },
  82: { label: "Violent showers", Icon: CloudRain },
  85: { label: "Snow showers", Icon: CloudSnow },
  86: { label: "Snow showers", Icon: CloudSnow },
  95: { label: "Thunderstorm", Icon: CloudLightning },
  96: { label: "Thunderstorm · hail", Icon: CloudLightning },
  99: { label: "Thunderstorm · hail", Icon: CloudLightning },
};

export default function WeatherWidget() {
  const state = useWeather();

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5" data-testid="weather-loading">
        <Compass size={11} className="animate-pulse" /> Locating…
      </div>
    );
  }

  if (state.status === "denied" || state.status === "unsupported" || state.status === "error") {
    return (
      <button
        onClick={state.refetch}
        data-testid="weather-enable"
        className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
        title={state.status === "denied" ? "Grant location permission to see weather" : "Enable weather"}
      >
        <MapPin size={11} /> Enable weather
      </button>
    );
  }

  const meta = (state.code != null && WMO[state.code]) || { label: "—", Icon: Sun };
  const Icon = meta.Icon;
  const hasForecast = state.hiF != null || state.loF != null || state.rain != null;
  return (
    <div className="inline-flex items-start gap-2" data-testid="weather-widget" title={state.place || "Local weather"}>
      <Icon size={18} weight="fill" className="text-brass mt-0.5" />
      <div className="leading-tight">
        <div className="text-sm font-medium text-foreground">
          {state.tempF != null ? `${state.tempF}°F` : "—"}
          {state.tempC != null && (
            <span className="text-muted-foreground text-xs ml-1.5">· {state.tempC}°C</span>
          )}
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          {meta.label}{state.place ? ` · ${state.place}` : ""}
        </div>
        {hasForecast && (
          <div className="mt-1 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest" data-testid="weather-forecast-peek">
            {state.hiF != null && (
              <span className="inline-flex items-center gap-0.5 border border-border/70 px-1.5 py-0.5 text-foreground/80" title="Today's high">
                <ArrowUp size={9} weight="bold" className="text-amber-500" />
                {state.hiF}°<span className="text-muted-foreground/70 ml-0.5">/ {state.hiC}°</span>
              </span>
            )}
            {state.loF != null && (
              <span className="inline-flex items-center gap-0.5 border border-border/70 px-1.5 py-0.5 text-foreground/80" title="Today's low">
                <ArrowDown size={9} weight="bold" className="text-primary" />
                {state.loF}°<span className="text-muted-foreground/70 ml-0.5">/ {state.loC}°</span>
              </span>
            )}
            {state.rain != null && (
              <span
                className={`inline-flex items-center gap-0.5 border px-1.5 py-0.5 ${
                  state.rain >= 50 ? "border-primary/60 text-primary" : "border-border/70 text-muted-foreground"
                }`}
                title="Chance of precipitation today"
              >
                <Drop size={9} weight="fill" />
                {state.rain}%
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
